import type { MouseEvent, ReactNode } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import s from './Masthead.module.css'

import { AppLink } from './AppLink'

/**
 * The two halves of the site. Named for what they hold rather than for how they are built:
 * a reader has no idea that one was a document and the other an application.
 */
const TABS = [
  { to: '/', label: 'Highlights' },
  { to: '/calendar', label: 'Calendar' },
] as const

export interface MastheadProps {
  /** Whoever is asking, or null. Nothing about the name is private — the reader typed it. */
  me: string | null
  /**
   * The account slot. The calendar passes a button with friends, invites and sign-out
   * behind it; the front page passes nothing and gets a link. It is the one part of the bar
   * whose behaviour legitimately differs between the two, so it is the one part injected.
   */
  account?: ReactNode
  /**
   * The answer has not arrived yet. Draws nothing in the slot rather than "Sign in", which
   * is the wrong answer for anybody who is signed in and the exact bug this bar has already
   * been through once.
   */
  pending?: boolean
  /** The reader's own face, drawn beside their name. */
  face?: ReactNode
  /** Gigs the briefing would still ask about. Zero draws nothing at all. */
  briefing?: number
  onBriefing?(): void
  /** Artist rows that are not artists. Zero draws nothing at all, same as the briefing. */
  tidy?: number
  onTidy?(): void
  /**
   * The bell, injected.
   *
   * A node rather than a count and a handler, like the search and the account slot beside
   * it: what is behind the bell fetches, marks itself read and holds its own open state,
   * and the bar has no business knowing that notices are a thing the server has.
   *
   * Unlike the two badges above it, this is drawn even with nothing waiting. Briefing and
   * Names are word-labelled buttons that would clutter the bar at nought, and both are
   * queues you are told about elsewhere; the bell is how a reader learns notices exist at
   * all, and a control that appears only once it has something to say cannot be used to go
   * back and look at what it said last week.
   */
  notices?: ReactNode
  /** Where your own name points. `/u/<you>` once there is a profile to point at. */
  mine?: string | null
  /**
   * Whether the editor is currently looking at the site as a reader, and how to change it.
   *
   * Absent for everybody who is not the editor — which used to mean *they* got no menu at
   * all, on the grounds that "a menu with one item in it is a menu nobody wants". True when
   * the only other item was Profile. The menu now holds Invite a friend and Sign out as
   * well, and gating the whole thing on this made both unreachable for everybody who is not
   * Bernardo. Now it gates one entry, not the menu.
   */
  normie?: boolean
  onNormie?(on: boolean): void
  /**
   * Whether to offer the editor's own pages: the studio, where the Instagram cards are made,
   * the names queue, and the latency dashboard. Named for the first of them, which was once
   * the only one.
   *
   * The editor's, and drawn from `me.isEditor` by the shell — which `ViewerProvider` masks in
   * normie mode, so this goes with every other editorial control and needs no second
   * condition of its own. A badge would be wrong: there is no queue behind the studio or the
   * dashboard and nothing waiting, which is why they are entries in the menu rather than words
   * on the bar.
   */
  studio?: boolean
  /**
   * The search, injected.
   *
   * One box, on every page, because search is not a property of one half of the site — it
   * began in the calendar's toolbar and a reader on the front page looking for an act had
   * to go to the calendar first to find the control that would have told them.
   *
   * Injected rather than built here for the same reason the account slot is: on the
   * calendar it can also narrow what is on screen, and the bar has no business knowing
   * that a calendar exists.
   */
  search?: ReactNode
}

export function Masthead({
  me,
  account,
  pending = false,
  face = null,
  briefing = 0,
  onBriefing,
  tidy = 0,
  onTidy,
  studio = false,
  notices = null,
  mine = null,
  normie,
  onNormie,
  search,
}: MastheadProps) {
  const here = useLocation()
  const goHome = (event: MouseEvent<HTMLAnchorElement>) => {
    // Not a modified press: cmd-click and middle-click are the browser's to answer, and
    // hijacking them would take away the new tab this link is now able to open.
    if (here.pathname !== '/' || event.metaKey || event.ctrlKey || event.shiftKey) return
    event.preventDefault()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className={s.top}>
      {/*
        * Home, and when you are already home, the top of it.
        *
        * The link is to `/`, so scrolled a long way down the front page it had nowhere to
        * send you and nothing moved — a control that plainly means "back to the start" doing
        * nothing at all. Reloading was the only way to get what pressing it looks like it
        * offers.
        *
        * Only when the address is not changing. Where it is, the router's own scroll
        * handling owns where the next page starts, and scrolling here as well would be two
        * things deciding one thing.
        */}
      <AppLink className={s.home} to="/" aria-label="London Gigs, home" onClick={goHome}>
        {/*
          `/logo/masthead`, not `/logo`. The full wordmark is 1448px wide because the sign-in
          page draws it at up to 320 CSS px; this slot is `clamp(34px, 4.4vw, 52px)` tall, so
          about 69px wide, and was being sent 369 KB for it on every page of the site. The
          masthead copy is 320px and 23 KB, which still covers this at three times the pixel
          density.
        */}
        <img className={s.wordmark} src="/logo/masthead" alt="London Gigs" />
      </AppLink>

      <nav className={s.nav} aria-label="Site">
        {TABS.map(tab => (
          // AppLink decides between a router link and a plain anchor, and applies `end`
          // to "/" so Highlights does not light up on every route beneath it. A venue page
          // belongs to neither half and lights neither.
          <AppLink key={tab.to} to={tab.to}>
            {tab.label}
          </AppLink>
        ))}
      </nav>

      {search}

      <span className={s.me}>
        {/*
          * What is waiting on you, beside your name rather than on it.
          *
          * The name links to your own page, so a badge on it would be counting one thing and
          * opening another. This counts the briefing and opens the briefing.
          *
          * Nothing at all when there is nothing, and nothing at all for a reader who is not
          * the editor — the pile is their queue, so a badge on a friend's bar would be
          * somebody else's unfinished work.
          */}
        {briefing > 0 && onBriefing ? (
          <button type="button" className={s.brief} onClick={onBriefing}>
            Briefing
            <span className={s.badge} aria-label={`${briefing} to decide on`}>
              {briefing}
            </span>
          </button>
        ) : null}
        {/*
          * The names queue, beside the briefing and drawn the same way — both are piles of
          * decisions only the curator can make, and neither is a page you would otherwise
          * think to open. Zero draws nothing, so the bar is quiet once the queue is empty
          * rather than carrying a permanent nought.
          */}
        {tidy > 0 && onTidy ? (
          <button type="button" className={s.brief} onClick={onTidy}>
            Names
            <span className={s.badge} aria-label={`${tidy} artist rows to judge`}>
              {tidy}
            </span>
          </button>
        ) : null}
        {account ??
          (pending ? null : me === null ? (
            <a href="/login">Sign in</a>
          ) : (
            /* A menu for everybody signed in, not only the editor. Behind it are Profile,
               Invite a friend and Sign out, none of which is editorial — and there is no
               other way to reach any of them. */
            <AccountMenu
              me={me}
              face={face}
              mine={mine ?? '/you'}
              normie={Boolean(normie)}
              studio={studio}
              {...(onNormie ? { onNormie } : {})}
            />
          ))}
        {/*
          * Last on the bar, past the name.
          *
          * Beside the name rather than beside the queues, because Briefing and Names are the
          * editor's work and this is anybody's — but on the far side of it, so the account
          * keeps the position it has always had. A bell inserted before the name pushes the
          * name leftwards on the one bar every page shares.
          *
          * It also puts the panel where it has the most room: it hangs from its trigger's
          * right edge, and the trigger is now the last thing on the bar.
          */}
        {notices}
      </span>
    </div>
  )
}


//: How long the menu waits after the pointer leaves before it shuts.
//:
//: The panel hangs six pixels below the name, and those six pixels belong to neither — so
//: travelling from one to the other leaves the whole control for a moment. Without the grace
//: the menu closes on the way to itself. The same number and the same reason as the
//: calendar's "+N more", which crosses a four-pixel gap.
const LINGER = 180

/**
 * Whether the pointer reading this page can hover.
 *
 * `hover: none` is the same question `EventCard.module.css` asks to decide whether to reveal
 * the marks, and it is asked here for the same reason: a control whose only gesture is a
 * hover is, on a phone, not a control. Read from `matchMedia` rather than from a user-agent
 * string or a touch-capability flag — a laptop with a touchscreen has both, and what decides
 * this is which one the reader is using.
 *
 * Subscribed rather than sampled once. A tablet that gains a trackpad, or a device emulation
 * toggled in devtools, changes the answer while the page is open, and a control stuck in the
 * wrong mode until reload is the bug this is meant to be the end of.
 */
function useCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(
    // `matchMedia` is absent under some test environments and on very old browsers. False is
    // the safe default: it leaves the pointer behaviour exactly as it was.
    () => typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches === true,
  )

  useEffect(() => {
    const query = window.matchMedia?.('(hover: none)')
    if (!query) return
    const answer = (event: MediaQueryListEvent) => setCoarse(event.matches)
    query.addEventListener('change', answer)
    return () => query.removeEventListener('change', answer)
  }, [])

  return coarse
}

/**
 * Your name, with what is behind it.
 *
 * A link until there was a second thing to reach. "See it as a reader does" lived on `/you`,
 * which is a page you have to already know about — and the whole point of it is checking
 * what somebody who does not know about your pages sees.
 *
 * Only drawn when there is a second item, so a reader still gets the plain link: a menu that
 * opens to reveal one entry is a worse control than the entry.
 *
 * Two gestures, one each, which is the arrangement that was asked for: pressing the name
 * goes to your page, and hovering it opens what is behind. It was one gesture doing both
 * jobs badly — a button that swallowed the press to toggle a panel whose first item was the
 * page the press looked like it was going to.
 *
 * Focus opens it as well, because a keyboard has no hover and the items inside are
 * reachable. The same pairing the calendar's "+N more" uses.
 */
function AccountMenu({
  me,
  face,
  mine,
  normie,
  studio,
  onNormie,
}: {
  me: string
  face: ReactNode
  mine: string
  normie: boolean
  /** Whether the editor's pages are this reader's. False for everybody who is not the editor. */
  studio: boolean
  /** Absent for everybody who is not the editor. Only the one entry depends on it. */
  onNormie?(on: boolean): void
}) {
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement>(null)
  const leaving = useRef<number | undefined>(undefined)
  const coarse = useCoarsePointer()
  const editorLabel = useId()

  const hold = () => {
    window.clearTimeout(leaving.current)
    setOpen(true)
  }
  const release = () => {
    leaving.current = window.setTimeout(() => setOpen(false), LINGER)
  }
  // Nothing left ticking after the bar goes, which would set state on a component that is
  // no longer mounted — the only thing this control does asynchronously.
  useEffect(() => () => window.clearTimeout(leaving.current), [])

  useEffect(() => {
    if (!open) return
    const away = (event: PointerEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false)
    }
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', away)
      document.removeEventListener('keydown', key)
    }
  }, [open])

  return (
    // `pointerleave` fires only once the pointer has left the panel as well, because the
    // panel is a real child of this element however far out of flow it is positioned. The
    // gap between them is the only part that is nobody's, and `LINGER` covers it.
    <div
      className={s.account}
      ref={box}
      {...(coarse
        ? {}
        : {
            onPointerEnter: hold,
            onPointerLeave: release,
            onFocus: hold,
            onBlur: release,
          })}
    >
      {/*
        * Two gestures on a pointer, one on a thumb.
        *
        * The pointer arrangement is the one that was asked for and is unchanged: pressing the
        * name goes to your page, hovering opens what is behind it. A touch screen has only the
        * press, so the press has to be the one that opens — otherwise Invite a friend, Normie
        * mode and Sign out have no route at all, which is what shipped.
        *
        * Nothing is lost by it: Profile is the first item in the panel, so your own page is one
        * tap further away rather than gone. The alternative — a second control beside the name
        * to open the menu — puts a fifth thing on a bar that already drops the reader's own
        * name at 390px for want of room.
        */}
      {coarse ? (
        <button
          type="button"
          className={s.mine}
          /*
           * Named, because at this width nothing inside it is readable to anybody.
           * `.myname` is `display: none` below 720px — deliberately, it bought the search box
           * back its width — and a hidden element is excluded from the accessible name, while
           * the face beside it is `aria-hidden`. So the button computed to no name at all, on
           * the one pointer type where it is the sole route to Invite a friend and Sign out.
           * The label restores what the visible name says on a wider screen; it does not bring
           * the name back to the bar.
           */
          aria-label={me}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen(now => !now)}
        >
          {face}
          <span className={s.myname}>{me}</span>
        </button>
      ) : (
        <AppLink
          to={mine}
          className={s.mine}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => setOpen(false)}
        >
          {face}
          {/* Wrapped so a phone can drop it and keep the face. See `.myname`. */}
          <span className={s.myname}>{me}</span>
        </AppLink>
      )}

      {open ? (
        <div className={s.menu} role="menu">
          <AppLink role="menuitem" to={mine} onClick={() => setOpen(false)}>
            Profile
          </AppLink>
          {/*
            * Anybody can invite, not just the curator: a calendar is worth more the more
            * people are on it, and one person being the gate on every friendship is how a
            * private site stays at twelve people for ever.
            */}
          <AppLink role="menuitem" to="/invite" onClick={() => setOpen(false)}>
            Invite a friend
          </AppLink>
          {/*
            * Normie mode. It masks `me.isEditor`, which every editorial control on the site
            * is drawn behind — so the star, the fold box, the curate panel and the briefing
            * all go at once.
            *
            * The one entry here that is the editor's, so it is the one entry that is absent
            * for everybody else rather than the reason they had no menu.
            *
            * `aria-pressed` and not a second menu item, because it is one switch with two
            * states rather than two places to go.
            */}
          {/*
            * The editor's own pages, under a label of their own: the studio, the names queue
            * and the latency dashboard. The only door to the studio and to the dashboard —
            * nothing else on the site links to either — and the one door to the names queue
            * that is there when the queue is empty, since its badge on the bar is not.
            *
            * A labelled group in this menu rather than a second control on the bar, which
            * already drops the reader's own name on a phone for want of room.
            *
            * Above Normie mode because these are somewhere to go and that is a switch, and
            * absent rather than disabled for everybody else — each route still answers a
            * typed address and its page says whose it is.
            */}
          {studio ? (
            <div role="group" aria-labelledby={editorLabel} className={s.group}>
              <span id={editorLabel} className={s.label}>
                Editor
              </span>
              <AppLink role="menuitem" to="/studio" onClick={() => setOpen(false)}>
                Studio
              </AppLink>
              <AppLink role="menuitem" to="/tidy" onClick={() => setOpen(false)}>
                Names
              </AppLink>
              <AppLink role="menuitem" to="/latency" onClick={() => setOpen(false)}>
                Latency
              </AppLink>
            </div>
          ) : null}
          {onNormie ? (
            <button
              type="button"
              role="menuitem"
              aria-pressed={normie}
              onClick={() => {
                setOpen(false)
                onNormie(!normie)
              }}
            >
              {normie ? 'Back to curating' : 'Normie mode'}
            </button>
          ) : null}
          {/*
            * Signing out, from the one place a person looks for it.
            *
            * It existed only on your own profile, three clicks in, which meant the menu
            * behind your name offered no way out of the site at all — the way out was
            * clearing a cookie by hand. `POST /logout` has always worked; nothing but the
            * entry was missing.
            *
            * Last, and after a rule, because it is the only item here that ends the session
            * rather than moving around inside it.
            */}
          <button
            type="button"
            role="menuitem"
            className={s.out}
            onClick={() => {
              setOpen(false)
              // A whole navigation rather than a route change: signing out changes who every
              // page on this site is for, and starting again is the cheapest way to be sure
              // nothing is left holding the last person's rows.
              void fetch('/logout', { method: 'POST', credentials: 'same-origin' }).then(() => {
                window.location.href = '/'
              })
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  )
}
